import { IonButton, IonContent, IonItem, IonPopover, IonText } from '@ionic/react';
import { menuController } from '@ionic/core/components';
import { FC, useState } from 'react';
import { DrawerSectionHeading } from '../components/DrawerSectionHeading';
import { ArrowRightArrowLeftIcon, ArrowRightIcon } from '../theme/Icons';
import { MenuItem } from '../components/MenuItem';
import { Divider } from '../components/Divider';
import { MemberAvatar } from '../household/MemberAvatar';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { ICareMap } from '../caremap/ICareMap';
import { createUseStyles } from 'react-jss';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { AddCareMapButton } from '../caremap/AddCareMapButton';
import { AppPrincipal, isCaremapViewer } from '../auth/AppPrincipal';

type IStyleKeys = 'content' | 'currentCareRecipient' | 'selected' | 'navListItems' | 'recipientMenuPopover' | 'recipientAvatar' | 'incompleteQuestionnaire';
interface IStyleProps {
    width: number;
};

const useStyles = createUseStyles<IStyleKeys, IStyleProps>({
    content: {
        '--padding-top': '36px'
    },

    currentCareRecipient: {
        fontSize: 18,
        fontWeight: 700,
        paddingRight: 20,
        paddingLeft: 30,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'end'
    },

    selected: {},
    navListItems: {
        marginTop: 12,

        '& > ion-item': {
            fontSize: 18,
            fontWeight: 500,
            '--inner-padding-start': '14px',

            '&:not($selected)': {
                '--background-hover-opacity': 0.2,
                '--background-hover': 'var(--ion-color-primary)',

                '& svg': {
                    fill: 'var(--ion-color-mediumtext)'
                    //stroke: 'var(--ion-color-mediumtext)'
                }
            },

            '&$selected': {
                '--background-hover-opacity': 0,
                '--background': 'rgba(var(--ion-color-primary-rgb), 0.2)',

                '& svg': {
                    fill: 'var(--ion-color-primary)'
                    //stroke: 'var(--ion-color-primary)'
                }
            }
        }
    },

    recipientMenuPopover: {
        '& hr': {
            marginBlockStart: 0,
            marginBlockEnd: 0
        },

        '&::part(content)': {
            minWidth: ({width}) => `${width-50}px`,
            maxWidth: ({width}) => `${width-50}px`
        }
    },

    recipientAvatar: {
        marginTop: 12,
        marginBottom: 12,
        marginInlineEnd: 18,
        borderRadius: '50%',
        border: '2px solid white',
        boxShadow: '2px 4px 12px #9a9a9a'
    },

    incompleteQuestionnaire: {
        display: 'block',
        fontSize: '0.95em',
        color: 'var(--ion-color-step-750)'
    }
});

export type INavigationContent = {
    popoverWidth: number;
    closeMenuHandler: () => void;
};

export const NavigationContent: FC<INavigationContent> = ({ popoverWidth, closeMenuHandler }) => {
    const classes = useStyles({ width: popoverWidth });
    const [isRecipientMenuOpen, setRecipientMenuOpen] = useState<boolean>(false);

    const tracking = useInstance(MixpanelService);
    const history = useInstance(HistoryViewModel);

    const isViewer = isCaremapViewer(useInstance(AppPrincipal));

    const careMapStateInstance = useInstance(CareMapState);
    useSubscription(() => careMapStateInstance);

    const careMaps = careMapStateInstance.getCareMaps();
    const hasMultipleCareMaps = careMaps.length > 1;
    const showAddButton = (!isViewer && careMaps.length === 1 && careMapStateInstance.state?.essentialMapComplete) || careMaps.length > 1;

    const onCareMapItemClick = () => {
        menuController.close();
        //history.push(`/caremaps/${careMapStateInstance.state?.careMapId}`);
    };

    const closeAndExecute = (handler: () => void) => {
        closeMenuHandler();
        setTimeout(() => {
            handler();
        }, 300);
    };

    const switchRecipient = (careMap: ICareMap) => {
        if (careMapStateInstance.state?.careMapId !== careMap.careMapId) {
            setRecipientMenuOpen(false);
            tracking.track('Care Recipient Switched');
            closeAndExecute(() => {
                careMapStateInstance.setCurrentCareMap(careMap.careMapId);
                history.push(`/caremaps/${careMap.careMapId}`);
            });
        }
    };

    const toggleRecipientMenu = () => {
        setRecipientMenuOpen(oldState => {
            return !oldState;
        });
    };

    return (
        <>
            <IonContent className={classes.content}>
                {careMapStateInstance.getCareMaps().length > 0 && (
                    <>
                        <div className={classes.currentCareRecipient}>
                            <div>
                                <DrawerSectionHeading text="Care Recipient" tight></DrawerSectionHeading>
                                <IonText>{careMapStateInstance.getPatientFullName() || 'New Recipient'}</IonText>
                            </div>
                            {hasMultipleCareMaps && (
                                <IonButton fill="clear" style={{marginBottom: -8}} onClick={toggleRecipientMenu} id="switch-recipient-button">
                                    <ArrowRightArrowLeftIcon />&nbsp;switch
                                </IonButton>
                            )}
                        </div>
                        <div className={classes.navListItems}>
                            <IonItem
                                button
                                lines="none"
                                onClick={onCareMapItemClick}
                                className={classes.selected}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="23" fill="currentColor" viewBox="0 0 25 23">
                                    <path fillRule="evenodd" d="M17.416.751c.537 0 1.034.287 1.302.752l5.298 9.176a1.504 1.504 0 0 1 0 1.504l-5.298 9.175a1.504 1.504 0 0 1-1.302.752H6.826a1.504 1.504 0 0 1-1.314-.772l-1.465-2.632a1 1 0 0 1 1.748-.973l1.323 2.377h10.011l5.012-8.68-5.012-8.679H7.107L5.72 5.155a1 1 0 0 1-1.732-1l1.531-2.652A1.504 1.504 0 0 1 6.821.751h10.595ZM2.88 8.683a1.005 1.005 0 1 1 1.005-1.74 1.005 1.005 0 0 1-1.005 1.74Zm-.368 5.996a1.005 1.005 0 1 0 1.74 1.005 1.005 1.005 0 0 0-1.74-1.005Z" clipRule="evenodd"/>
                                    <circle cx="1.373" cy="11.497" r="1.005" transform="rotate(120 1.373 11.497)"/>
                                </svg>
                                <IonText style={{marginLeft: 16}}>Care Map</IonText>
                            </IonItem>
                        </div>

                        {showAddButton && (
                            <div style={{margin: 24}}>
                                <AddCareMapButton closeMenu={closeMenuHandler} />
                            </div>
                        )}
                    </>
                )}
            </IonContent>

            {/* switch care recipient menu */}
            {hasMultipleCareMaps && (
                <IonPopover
                    alignment="end"
                    trigger="switch-recipient-button"
                    isOpen={isRecipientMenuOpen}
                    onDidDismiss={() => setRecipientMenuOpen(false)}
                    className={classes.recipientMenuPopover}
                >
                    <MenuItem data-id="switch-recipient" label="Switch Care Recipient" heading />
                    <Divider />
                    {careMaps.map((map, idx) => {
                        const isLast = idx+1 === careMaps.length;
                        return map.careMapId !== careMapStateInstance.state?.careMapId ? (
                            <div key={map.careMapId}>
                                <MenuItem
                                    data-id={map.careMapId}
                                    onClick={() => switchRecipient(map)}
                                    label={(
                                        <>
                                            {careMapStateInstance.getPatientFullName(map) || 'New Recipient'}
                                            {!map.essentialMapComplete && (
                                                <IonText
                                                    data-type="incomplete-questionnaire"
                                                    className={classes.incompleteQuestionnaire}>care map in progress
                                                </IonText>
                                            )}
                                        </>
                                    )}>
                                    <div slot="start" className={classes.recipientAvatar} style={{marginBottom: isLast ? 16 : undefined}}>
                                        <MemberAvatar small member={{photo: map.patient.photo, color: map.patient.color, avatar: map.patient.avatar}} />
                                    </div>
                                    <ArrowRightIcon slot="end" color="medium" />
                                </MenuItem>
                                {!isLast && <Divider />}
                            </div>
                        ) : null;
                    })}
                </IonPopover>
            )}
        </>
    );
};
